import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  Typography,
  Button,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import { format } from "date-fns";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import Page from "./Page";
import CallDocketModal from "../modals/CallDocketModal";
import useData from "../../hooks/useData";
import useCall from "../../hooks/useCall";
import useSupervisors from "../../hooks/useSupervisors";
import useProject from "../../hooks/useProject";
import usePage from "../../hooks/usePage";
import useLocalization from "../../hooks/useLocalization";

const CallDurationOutliers = () => {
  const navigate = useNavigate();
  const { executeAction } = useData();
  const { getCallFullListById, getCallById } = useCall();
  const { showMessage } = usePage();
  const { provinceList } = useLocalization();
  const { role, userId: currentUserId } = useSelector(
    (state) => state.authentication,
  );
  const { supervisors = [] } = useSupervisors();
  const { projects = [], getProjects } = useProject();

  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [pageNumber, setPageNumber] = useState(0);
  const [recordsPerPage] = useState(25);
  const [loading, setLoading] = useState(false);
  const [selectedSupervisorId, setSelectedSupervisorId] = useState("");
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [selectedProvince, setSelectedProvince] = useState("");

  const [selectedCall, setSelectedCall] = useState(null);
  const [docketOpen, setDocketOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [approveTargetCall, setApproveTargetCall] = useState(null);
  const [approvalNote, setApprovalNote] = useState("");

  const safeSupervisors = Array.isArray(supervisors) ? supervisors : [];
  const safeProjects = Array.isArray(projects) ? projects : [];
  const provinces = provinceList();

  const canApproveOutliers = [
    "supervisor",
    "admin",
    "finance",
    "sysadmin",
  ].includes(role);

  const handleToolBarClick = (item) => {
    switch (item) {
      case "callDashboard":
        navigate("/home");
        break;
      case "syncDashboard":
        navigate("/syncdashboard");
        break;
      case "surveyDashboard":
        navigate("/surveymetrics");
        break;
      case "orderDashboard":
        navigate("/orderdashboard");
        break;
      case "clientDashboard":
        navigate("/clientdashboard");
        break;
      case "repDashboard":
        navigate("/repdashboard");
        break;
      default:
        break;
    }
  };

  const loadOutliers = async (targetPage = pageNumber) => {
    if (!canApproveOutliers) {
      setRecords([]);
      setTotal(0);
      return;
    }

    setLoading(true);
    try {
      const results = await executeAction({
        entityName: "Call",
        action: "getDurationOutliersPage",
        data: {
          pageNumber: targetPage,
          recordsPerPage,
          supervisorId: selectedSupervisorId || null,
          projectId: selectedProjectId || null,
          province: selectedProvince || null,
        },
      });

      if (results?.isSuccessful) {
        const payload = results.data || {};
        setRecords(Array.isArray(payload.records) ? payload.records : []);
        setTotal(Number(payload.total) || 0);
      } else {
        setRecords([]);
        setTotal(0);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getProjects();
  }, []);

  useEffect(() => {
    if (!currentUserId) return;

    const isManagerInList = safeSupervisors.some(
      (supervisor) =>
        String(supervisor.id || supervisor._id || "") === String(currentUserId),
    );

    if (isManagerInList) {
      setSelectedSupervisorId(currentUserId);
    }
  }, [currentUserId, safeSupervisors]);

  useEffect(() => {
    loadOutliers();
  }, [
    pageNumber,
    canApproveOutliers,
    selectedSupervisorId,
    selectedProjectId,
    selectedProvince,
  ]);

  const handleOpenDocket = async (callId) => {
    const targetCallId = callId || null;
    if (!targetCallId) {
      showMessage("Unable to load call docket", "error");
      return;
    }

    const call =
      (await getCallById(targetCallId)) ||
      (await getCallFullListById(targetCallId));

    if (call) {
      setSelectedCall(call);
      setDocketOpen(true);
      return;
    }

    showMessage("Unable to load call docket", "error");
  };

  const handleCloseDocket = () => {
    setDocketOpen(false);
    setSelectedCall(null);
  };

  const handleDocketRefresh = async () => {
    await loadOutliers();
  };

  const handleOpenApproveDialog = (call) => {
    setApproveTargetCall(call);
    setApprovalNote("");
    setApproveDialogOpen(true);
  };

  const handleCloseApproveDialog = () => {
    setApproveDialogOpen(false);
    setApproveTargetCall(null);
    setApprovalNote("");
  };

  const handleApproveOutlier = async () => {
    const callId = approveTargetCall?.id || approveTargetCall?._id;
    if (!callId) return;

    const results = await executeAction({
      entityName: "Call",
      action: "approveDurationOutlier",
      data: {
        id: callId,
        approvalNote,
      },
    });

    if (results?.isSuccessful) {
      showMessage("Outlier approved", "success");
      handleCloseApproveDialog();
      await loadOutliers();
      return;
    }

    showMessage(results?.message || "Unable to approve outlier", "error");
  };

  const formatDate = (value) => {
    if (!value) return "-";
    return format(new Date(value), "MMM dd, yyyy");
  };

  const formatLocationAddress = (location) => {
    const address = location?.address || {};
    const parts = [
      address.streetAddress,
      address.otherAddress,
      address.city,
      address.province,
      address.postal,
    ]
      .map((part) => (part ? String(part).trim() : ""))
      .filter((part) => part.length > 0);

    return parts.join(", ");
  };

  const formatLocationDisplayName = (location) => {
    const name = String(location?.name || "").trim();
    const locationNumber = String(location?.locationNumber || "").trim();

    if (!name) return "-";
    if (!locationNumber) return name;

    return `${name} #${locationNumber}`;
  };

  const totalPages = Math.max(Math.ceil(total / recordsPerPage), 1);

  return (
    <>
      <Page
        pageName="callDurationOutliers"
        includeToolbar={true}
        onToolItemClick={handleToolBarClick}
        items={[
          { name: "callDashboard", iconName: "CalendarMonth" },
          { name: "syncDashboard", iconName: "CloudSync" },
          { name: "surveyDashboard", iconName: "Assignment" },
          { name: "orderDashboard", iconName: "ShoppingCart" },
          { name: "clientDashboard", iconName: "Business" },
          { name: "repDashboard", iconName: "People" },
        ]}
      >
        {!canApproveOutliers ? (
          <Typography variant="body1">
            You do not have access to this page.
          </Typography>
        ) : (
          <>
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6">
                Calls Requiring Outlier Approval ({total})
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
              >
                Completed calls where duration exceeds project duration +
                variance, or mileage exceeds the configured threshold.
              </Typography>
            </Box>

            <Box sx={{ mb: 2, display: "flex", gap: 2, flexWrap: "wrap" }}>
              <FormControl sx={{ minWidth: 260 }}>
                <InputLabel>Supervisor</InputLabel>
                <Select
                  value={selectedSupervisorId}
                  label="Supervisor"
                  onChange={(e) => {
                    setPageNumber(0);
                    setSelectedSupervisorId(e.target.value);
                  }}
                >
                  <MenuItem value="">
                    <em>All Supervisors</em>
                  </MenuItem>
                  {safeSupervisors.map((supervisor) => (
                    <MenuItem
                      key={supervisor.id}
                      value={supervisor.id}
                    >
                      {supervisor.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl sx={{ minWidth: 300 }}>
                <InputLabel>Project</InputLabel>
                <Select
                  value={selectedProjectId}
                  label="Project"
                  onChange={(e) => {
                    setPageNumber(0);
                    setSelectedProjectId(e.target.value);
                  }}
                >
                  <MenuItem value="">
                    <em>All Projects</em>
                  </MenuItem>
                  {safeProjects.map((project) => (
                    <MenuItem
                      key={project.id}
                      value={project.id}
                    >
                      {project.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl sx={{ minWidth: 220 }}>
                <InputLabel>Province</InputLabel>
                <Select
                  value={selectedProvince}
                  label="Province"
                  onChange={(e) => {
                    setPageNumber(0);
                    setSelectedProvince(e.target.value);
                  }}
                >
                  <MenuItem value="">
                    <em>All Provinces</em>
                  </MenuItem>
                  {provinces.map((province) => (
                    <MenuItem
                      key={province.id}
                      value={province.id}
                    >
                      {province.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "2px solid #ddd" }}>
                  <th style={{ padding: "8px", textAlign: "left" }}>Date</th>
                  <th style={{ padding: "8px", textAlign: "left" }}>Rep</th>
                  <th style={{ padding: "8px", textAlign: "left" }}>Project</th>
                  <th style={{ padding: "8px", textAlign: "left" }}>
                    Location
                  </th>
                  <th style={{ padding: "8px", textAlign: "right" }}>
                    Call Duration
                  </th>
                  <th style={{ padding: "8px", textAlign: "right" }}>
                    Allowed
                  </th>
                  <th style={{ padding: "8px", textAlign: "right" }}>
                    Over By
                  </th>
                  <th style={{ padding: "8px", textAlign: "right" }}>
                    Mileage
                  </th>
                  <th style={{ padding: "8px", textAlign: "right" }}>
                    Mileage Limit
                  </th>
                  <th style={{ padding: "8px", textAlign: "right" }}>
                    Mileage Over
                  </th>
                  <th style={{ padding: "8px", textAlign: "left" }}>Reason</th>
                  <th style={{ padding: "8px", textAlign: "center" }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {records.map((call) => (
                  <tr
                    key={call.id}
                    style={{ borderBottom: "1px solid #eee" }}
                  >
                    <td style={{ padding: "8px" }}>
                      {formatDate(call.scheduleDate)}
                    </td>
                    <td style={{ padding: "8px" }}>
                      {call.repName || "Unassigned"}
                    </td>
                    <td style={{ padding: "8px" }}>
                      {call.project?.name || "-"}
                    </td>
                    <td style={{ padding: "8px" }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 500 }}
                      >
                        {formatLocationDisplayName(call.location)}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                      >
                        {formatLocationAddress(call.location) || "-"}
                      </Typography>
                    </td>
                    <td style={{ padding: "8px", textAlign: "right" }}>
                      {call.callDuration || 0}
                    </td>
                    <td style={{ padding: "8px", textAlign: "right" }}>
                      {call.allowedDuration || 0}
                    </td>
                    <td style={{ padding: "8px", textAlign: "right" }}>
                      {call.overBy || 0}
                    </td>
                    <td style={{ padding: "8px", textAlign: "right" }}>
                      {call.mileage || 0}
                    </td>
                    <td style={{ padding: "8px", textAlign: "right" }}>
                      {call.mileageThreshold || 0}
                    </td>
                    <td style={{ padding: "8px", textAlign: "right" }}>
                      {call.mileageOverBy || 0}
                    </td>
                    <td style={{ padding: "8px" }}>
                      {call.isDurationOutlier && call.isMileageOutlier
                        ? "Duration + Mileage"
                        : call.isDurationOutlier
                          ? "Duration"
                          : call.isMileageOutlier
                            ? "Mileage"
                            : "-"}
                    </td>
                    <td style={{ padding: "8px", textAlign: "center" }}>
                      <Stack
                        direction="row"
                        spacing={1}
                        justifyContent="center"
                      >
                        <IconButton
                          size="small"
                          color="primary"
                          title="View"
                          onClick={() => handleOpenDocket(call.id || call._id)}
                          disabled={loading}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="success"
                          title="Approve"
                          onClick={() => handleOpenApproveDialog(call)}
                          disabled={loading}
                        >
                          <CheckCircleIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </td>
                  </tr>
                ))}
                {!loading && records.length === 0 && (
                  <tr>
                    <td
                      colSpan={12}
                      style={{ padding: "16px", textAlign: "center" }}
                    >
                      No outliers pending approval.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            <Stack
              direction="row"
              spacing={1}
              sx={{ mt: 2 }}
            >
              <Button
                variant="outlined"
                disabled={pageNumber <= 0 || loading}
                onClick={() =>
                  setPageNumber((current) => Math.max(current - 1, 0))
                }
              >
                Previous
              </Button>
              <Button
                variant="outlined"
                disabled={loading || pageNumber + 1 >= totalPages}
                onClick={() => setPageNumber((current) => current + 1)}
              >
                Next
              </Button>
              <Typography
                variant="body2"
                sx={{ alignSelf: "center", ml: 1 }}
              >
                Page {pageNumber + 1} of {totalPages}
              </Typography>
            </Stack>
          </>
        )}
      </Page>

      <CallDocketModal
        call={selectedCall}
        open={docketOpen}
        onClose={handleCloseDocket}
        onRefresh={handleDocketRefresh}
      />

      <Dialog
        open={approveDialogOpen}
        onClose={handleCloseApproveDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Approve Outlier</DialogTitle>
        <DialogContent>
          <Typography
            variant="body2"
            sx={{ mb: 2 }}
          >
            {approveTargetCall?.project?.name || "Project"} •{" "}
            {formatLocationDisplayName(approveTargetCall?.location)}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Approval Note"
            value={approvalNote}
            onChange={(e) => setApprovalNote(e.target.value)}
            placeholder="Optional note for approval"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseApproveDialog}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleApproveOutlier}
          >
            Approve
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CallDurationOutliers;
